// Command Center, Riso Press layout (POC). The morning brief as a freshly
// pulled risograph print: fluorescent inks on natural stock, misregistered at
// rest and snapping into registration where attention lands.
// Data and actions: useMorningBrief.js. Toggle: CommandCenterSwitch.jsx.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, ArrowRight, Bot, CheckCircle2, ChevronDown, Compass, Flame, Hourglass, Inbox, NotebookPen, Plug, Undo2 } from 'lucide-react';
import { entityPath } from '../lib/productInsights';
import { BrainStatus } from '../components/brain/BrainUI';
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { TYPE_NAMES, clock, humanDates, safeName, useMorningBrief, verb } from './useMorningBrief';
import './CommandCenterRiso.css';

// Each kind of prompt prints in its own ink.
const KINDS = {
  issue: { type: 'issues', label: 'Critical issue', tag: 'Issue · T1', ink: 'orange' },
  readiness: { type: 'initiatives', label: 'Initiative', tag: 'Initiative', ink: 'green' },
  validated: { type: 'problems', label: 'Problem', tag: 'Problem', ink: 'teal' },
  stronger: { type: 'problems', label: 'Problem', tag: 'Problem', ink: 'teal' },
  stale: { type: 'problems', label: 'Stale evidence', tag: 'Stale evidence', ink: 'yellow' },
  decision: { type: 'decisions', label: 'Decision review', tag: 'Decision', ink: 'pink' },
  experiment: { type: 'experiments', label: 'Experiment', tag: 'Experiment', ink: 'purple' },
};
const TYPE_INKS = { issues: 'orange', initiatives: 'green', problems: 'teal', evidence: 'teal', decisions: 'pink', experiments: 'purple', results: 'purple', learnings: 'purple', hypotheses: 'purple', objectives: 'yellow', inbox: 'blue', reminders: 'yellow' };
// Urgency is carried by weight and width: fires print heaviest and widest.
const TYPE_AXES = { 0: [800, 100], 1: [760, 100], 2: [540, 92], 3: [430, 86] };

// Two passes of ink: the second sits slightly off-register until focused.
function Overprint({ children, className = '' }) {
  return <span className={`riso-op ${className}`}><span className="riso-op-a">{children}</span><span className="riso-op-b" aria-hidden="true">{children}</span></span>;
}

// A hand-drawn ring, inked around whatever has focus.
const Ring = () => <svg className="riso-ring" viewBox="0 0 100 60" preserveAspectRatio="none" aria-hidden="true"><path pathLength="1" d="M58 5C30-1 3 10 6 30c2 15 24 27 50 25 25-2 42-14 39-30C92 11 76 3 49 7l-6 2" /></svg>;

// An amplitude-modulated halftone: dot size falls off with distance from the
// field's origin corner, the way a riso screen renders a gradient.
function Halftone({ cols = 22, rows = 14, origin = 'top-right', strength = 1, className = '' }) {
  const dots = useMemo(() => {
    const out = [];
    const [oy, ox] = origin.split('-');
    for (let y = 0; y < rows; y += 1) for (let x = 0; x < cols; x += 1) {
      const dx = (ox === 'right' ? cols - 1 - x : x) / cols;
      const dy = (oy === 'bottom' ? rows - 1 - y : y) / rows;
      const r = Math.max(0, 1 - Math.hypot(dx * 1.1, dy * 1.4)) * 4.6 * strength;
      if (r > 0.35) out.push(<circle key={`${x}-${y}`} cx={x * 10 + (y % 2 ? 5 : 0) + 5} cy={y * 10 + 5} r={Math.min(r, 5.4)} />);
    }
    return out;
  }, [cols, rows, origin, strength]);
  return <svg className={`riso-halftone ${className}`} viewBox={`0 0 ${cols * 10} ${rows * 10}`} preserveAspectRatio="xMaxYMin slice" aria-hidden="true">{dots}</svg>;
}

// Registration target, printed at the corners of the sheet.
const Target = ({ className }) => <svg className={`riso-target ${className}`} viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="6" /><path d="M12 0v24M0 12h24" /></svg>;

function Actions({ s, big, busy, onAct, onSnooze, onDismiss, onUndo, onWake }) {
  const { item, status } = s;
  if (status.stamped) return <div className="riso-act"><span className="riso-void" role="status">Void</span><button type="button" className="riso-quiet" onClick={onUndo} disabled={!status.recordId}><Undo2 aria-hidden="true" />Undo</button></div>;
  return <div className="riso-act">
    <Link className={big ? 'riso-cta' : 'riso-go'} to={item.href} onClick={onAct} aria-label={item.action}>{big ? item.action : verb(item.action)}<ArrowRight aria-hidden="true" /></Link>
    {status.snoozedUntil
      ? <button type="button" className="riso-quiet" onClick={onWake} disabled={busy}>Wake now</button>
      : <span className="riso-act-secondary"><button type="button" className="riso-quiet" onClick={onSnooze} disabled={busy}><AlarmClock aria-hidden="true" />Snooze</button><span className="riso-act-gap" aria-hidden="true" /><button type="button" className="riso-quiet riso-dismiss" onClick={onDismiss} disabled={busy}>Dismiss</button></span>}
  </div>;
}

// The run is numbered from the poster (01) onward; fires and the shelf carry a
// mark instead, so every number on the page is a place in today's order.
function Row({ s, n, mark: Mark, first, ...handlers }) {
  const { item, kind, meta, status, isNew } = s;
  const until = status.snoozedUntil && new Date(status.snoozedUntil);
  const [wght, wdth] = TYPE_AXES[item.priority] || TYPE_AXES[2];
  return <article tabIndex={-1} data-strip={item.id} data-guide={first ? 'brief-first' : undefined} aria-label={`${kind.label}: ${item.title}`}
    className={`riso-row ink-${kind.ink}${status.stamped ? ' is-void' : ''}`} style={{ '--w': wght, '--wd': `${wdth}%`, viewTransitionName: safeName(item.id) }}>
    <span className={`riso-num${Mark ? ' is-mark' : ''}`} aria-hidden="true">{Mark ? <Mark /> : <Overprint>{String(n).padStart(2, '0')}</Overprint>}<Ring /></span>
    <div className="riso-row-body">
      <h3>{item.title}</h3>
      <div className="riso-tags"><span className="riso-chip">{kind.tag}</span>{(until || meta) && <span className="riso-meta">{until ? `Snoozed until ${until.toLocaleDateString(undefined, { weekday: 'short' })} ${until.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : meta}</span>}{isNew && <span className="riso-new">New</span>}</div>
      <p>{humanDates(item.detail)}</p>
    </div>
    <Actions s={s} {...handlers} />
  </article>;
}

function Poster({ s, first, ...handlers }) {
  const { item, kind, meta, status, isNew } = s;
  return <article tabIndex={-1} data-strip={item.id} data-guide={first ? 'brief-first' : undefined} aria-label={`${kind.label}: ${item.title}`}
    className={`riso-poster ink-${kind.ink}${status.stamped ? ' is-void' : ''}`} style={{ viewTransitionName: safeName(item.id) }}>
    <Halftone strength={item.priority <= 1 ? 1 : 0.7} />
    <div className="riso-poster-head">
      <span className="riso-poster-num" aria-hidden="true"><Overprint>01</Overprint></span>
      <h3><Overprint>{item.title}</Overprint></h3>
    </div>
    <div className="riso-tags"><span className="riso-chip">{kind.tag}</span>{meta && <span className="riso-meta">{meta}</span>}{isNew && <span className="riso-new">New</span>}</div>
    <p>{humanDates(item.detail)}</p>
    <Actions s={s} big {...handlers} />
  </article>;
}

function Changes({ arrivals, loading, now }) {
  if (!arrivals.length) return <p className="riso-note">{loading ? 'Reading your records…' : 'Nothing changed since then.'}</p>;
  return <ol className="riso-timeline">{arrivals.map(n => <li key={`${n.type}:${n.id}`} className={`ink-${TYPE_INKS[n.type] || 'blue'}`}>
    <time dateTime={n.at}>{clock(n.at, now)}</time>
    <i aria-hidden="true" />
    <Link to={entityPath(n.type, n.id)}><strong>{n.label}</strong><small>{n.change || `Updated ${TYPE_NAMES[n.type] || n.type}`}</small></Link>
  </li>)}</ol>;
}

function Section({ id, title, count, label, children, className = '' }) {
  return <section id={`riso-${id}`} className={`riso-section ${className}`} aria-labelledby={title ? `riso-h-${id}` : undefined} aria-label={title ? undefined : label}>
    {title && <h2 id={`riso-h-${id}`}><span>{title}</span>{count != null && <span className="riso-count">{count}</span>}</h2>}
    {children}
  </section>;
}

export default function CommandCenterRiso({ layoutSwitch = null }) {
  const b = useMorningBrief({ kinds: KINDS, movingClass: 'riso-moving' });
  const { data, loading, workspace, guide, now, sinceText, error, busy, strips, fires, today, watch, clearedToday, firstId, arrivals, record, remove, onKeyDown } = b;
  const [showAll, setShowAll] = useState(false);
  const [printed, setPrinted] = useState(false);
  useEffect(() => { if (!loading && !printed) { const t = setTimeout(() => setPrinted(true), 1600); return () => clearTimeout(t); } }, [loading, printed]);

  const next = today[0];
  const run = today.slice(1);
  const visibleRun = showAll ? run : run.slice(0, 6);
  const handlers = s => ({
    busy: busy.has(s.item.id),
    onAct: () => guide?.act(s.item.id),
    onSnooze: () => record(s.item, 'snooze'),
    onDismiss: () => record(s.item, 'dismiss'),
    onUndo: () => remove(s.item, [s.status.recordId]),
    onWake: () => remove(s.item, s.status.records.map(f => f.id)),
  });
  const reduce = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const jump = id => {
    // Narrow screens fold the changes timeline into a disclosure after the fires.
    const compact = id === 'changes' && document.querySelector('.riso-changes-compact');
    const target = compact && compact.offsetParent !== null ? compact : document.getElementById(`riso-${id}`);
    if (compact && target === compact) compact.open = true;
    target?.scrollIntoView({ block: 'start', behavior: reduce() ? 'auto' : 'smooth' });
  };
  const weekday = now.toLocaleDateString(undefined, { weekday: 'long' });
  const sinceDay = sinceText ? sinceText.split(' ')[0] : null;
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const noBrief = !loading && !strips.length;
  const snoozeMark = s => (s.status.snoozedUntil ? AlarmClock : Hourglass);

  return <div className={`riso${printed ? '' : ' is-printing'}`}>
    <header className="riso-mast">
      <Target className="is-left" /><Target className="is-right" />
      <div className="riso-mast-top">
        <p className="riso-dateline">{[workspace?.name, now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })].filter(Boolean).join(' · ')}</p>
        <div className="riso-tools">{layoutSwitch}<Link className="riso-capture" to="/inbox" data-guide="capture-signal"><Inbox aria-hidden="true" />Capture a signal</Link></div>
      </div>
      <h1 className="riso-headline">
        <span className="v2-sr">Your morning brief: </span>
        {loading ? <Overprint>Pulling today’s print…</Overprint> : <>
          <Overprint>{weekday}.</Overprint>{' '}
          <span className="riso-nw">{fires.length
            ? <button type="button" className="riso-hl ink-orange" onClick={() => jump('fires')}>{plural(fires.length, 'fire', 'fires')}</button>
            : <span className="riso-hl ink-green">no fires</span>},</span>{' '}
          <span className="riso-nw">{today.length
            ? <button type="button" className="riso-hl ink-pink" onClick={() => jump('first')}>{today.length} to move today</button>
            : <span className="riso-hl ink-yellow">nothing due today</span>},</span>{' '}
          <button type="button" className="riso-hl ink-teal" onClick={() => jump('changes')}>{plural(arrivals.length, 'change', 'changes')}</button>{' '}
          <Overprint>{sinceDay ? `since ${sinceDay}.` : 'lately.'}</Overprint>
          {clearedToday > 0 && <>{' '}<Overprint className="riso-cleared">{clearedToday} cleared.</Overprint></>}
        </>}
      </h1>
    </header>
    <BrainStatus quietRefresh />
    {error && <div className="riso-error" role="alert">{error}</div>}

    <div className="riso-grid">
      <div className="riso-main" onKeyDown={onKeyDown}>
        {loading ? <div className="riso-loading" aria-hidden="true"><span /><span /><span /></div> : noBrief
          ? <div className="riso-empty"><h2><Overprint>{data.problems.length ? 'Nothing urgent in your records.' : 'Start with a customer problem.'}</Overprint></h2><p>Capture a signal in Inbox, or add a problem in Discovery. Your brief will reflect the evidence and initiatives you save.</p></div>
          : <>
            {fires.length > 0 && <Section id="fires" title="Stop press" count={fires.length} className="riso-stop">
              <Halftone className="is-band" cols={26} rows={8} origin="top-right" strength={0.95} />
              <div className="riso-rows">{fires.map(s => <Row key={s.item.id} s={s} mark={Flame} first={s.item.id === firstId} {...handlers(s)} />)}</div>
            </Section>}
            <details className="riso-changes-compact">
              <summary><span className="riso-hl ink-teal">{plural(arrivals.length, 'change', 'changes')}</span> {sinceText ? `since ${sinceText}` : 'lately'}<ChevronDown aria-hidden="true" /></summary>
              <Changes arrivals={arrivals} loading={loading} now={now} />
            </details>
            <Section id="first" label="Do this first" className="riso-first">
              {next ? <Poster s={next} first={next.item.id === firstId} {...handlers(next)} /> : <p className="riso-clear"><CheckCircle2 aria-hidden="true" />Nothing needs a decision today.</p>}
            </Section>
            {run.length > 0 && <Section id="run" title="Then" count={run.length}>
              <div className="riso-rows">{visibleRun.map((s, i) => <Row key={s.item.id} s={s} n={i + 2} {...handlers(s)} />)}</div>
              {run.length > visibleRun.length && <button type="button" className="riso-more" onClick={() => setShowAll(true)}>Show {run.length - visibleRun.length} more</button>}
            </Section>}
            <Section id="shelf" title="On the shelf" count={watch.length} className="riso-shelf">
              {watch.length
                ? <div className="riso-rows">{watch.map(s => <Row key={s.item.id} s={s} mark={snoozeMark(s)} first={s.item.id === firstId} {...handlers(s)} />)}</div>
                : <p className="riso-clear">Snoozed prompts and stale evidence wait here.</p>}
            </Section>
            <p className="riso-keys" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move <kbd>Enter</kbd> open <kbd>S</kbd> snooze <kbd>E</kbd> dismiss <kbd>Z</kbd> undo</p>
          </>}
      </div>

      <aside className="riso-side">
        <Section id="changes" title={sinceText ? `Since ${sinceText}` : 'Recently'} className="riso-since">
          <Changes arrivals={arrivals} loading={loading} now={now} />
        </Section>
        <nav className="riso-jump" aria-label="Shortcuts">
          {guide?.activation?.path === 'jira' && !data.imports.length && <Link to="/onboarding"><Plug aria-hidden="true" />Setup &amp; integrations</Link>}
          <Link to="/autopilot"><Bot aria-hidden="true" />Agent team</Link>
          <Link to="/brain"><Compass aria-hidden="true" />Product Brain</Link>
          <Link to="/decisions"><NotebookPen aria-hidden="true" />Record a decision</Link>
          <Link to="/learning"><CheckCircle2 aria-hidden="true" />Review learning</Link>
        </nav>
        <WorkspaceInsights area="command_center" />
      </aside>
    </div>
  </div>;
}
